import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { reportError } from "../services/eventBus";
import { Button } from "../components/Button";
import { Spinner } from "../components/Spinner";
import { getUserManager } from "./userManager";

const HOME = "/uw/recent";

/** OIDC redirect target: completes the code exchange, then returns to the page the sign-in started from. */
export function CallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;
    const raw = sessionStorage.getItem("preLoginUrl");
    const savedUrl = raw && raw.startsWith("/") && !raw.startsWith("//") ? raw : null;

    getUserManager()
      .signinRedirectCallback()
      .then(() => {
        sessionStorage.removeItem("preLoginUrl");
        navigate(savedUrl ?? HOME, { replace: true });
      })
      .catch(async (caught: unknown) => {
        const failure = caught instanceof Error ? caught : new Error(String(caught));
        // "No matching state": the callback was already processed (back button) or the session was cleared.
        if (failure.message.includes("No matching state")) {
          const existing = await getUserManager().getUser();
          navigate(existing && !existing.expired ? (savedUrl ?? HOME) : "/", { replace: true });
          return;
        }
        reportError(failure, "oidc.callback");
        setError(failure.message || "Authentication failed");
      });
  }, [navigate]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-6">
        <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-lg">
          <h1 className="mb-2 text-xl font-bold text-brand-navy">Sign-in did not complete</h1>
          <p className="mb-6 text-gray-600">{error}</p>
          <Button onClick={() => navigate("/", { replace: true })}>Try again</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="flex flex-col items-center gap-3 text-gray-600">
        <Spinner />
        <p>Completing sign-in...</p>
      </div>
    </div>
  );
}
